import { useCallback } from 'react'
import { useAppStore } from '@/lib/store/useAppStore'
import { useNetworkStore } from '@/lib/store/useNetworkStore'
import { queueManager } from '@/lib/sync/QueueManager'
import { syncService } from '@/lib/sync/SyncService'
import { eventBus } from '@/lib/eventBus/events'
import { db } from '@/lib/db/database'
import type { Booking, BookingStatus } from '@/lib/types/booking'
import { useQueryClient } from '@tanstack/react-query'

function buildTempBooking(data: Partial<Booking>): Booking {
  const now = new Date().toISOString()
  const date = data.date ?? ''
  const time = data.time ?? '00:00'
  const dateTime = date && time ? new Date(`${date}T${time}:00`).toISOString() : now
  const duration = data.duration ?? 90
  const endDateTime = new Date(new Date(dateTime).getTime() + duration * 60_000).toISOString()
  const tempId = `temp-${Date.now()}`

  const base = {
    bookosId: data.bookosId ?? `manual-${Date.now()}`,
    date,
    time,
    dateTime,
    endDateTime,
    people: data.people ?? 1,
    duration,
    status: data.status ?? 'request',
    source: data.source ?? 'phone',
    languageCode: 'en',
    comment: data.comment,
    note: data.note,
    sendNotification: false,
    seenByRestaurant: true,
    activityAt: now,
    guest: data.guest ?? { name: '', notificationSms: false, notificationEmail: false },
    bookingTables: [],
    createdAt: now,
    updatedAt: now,
    ...data,
  }

  // Pin id and documentId last so caller-supplied data can't override them
  return { ...base, id: -1, documentId: tempId } as Booking
}

export function useBookingActions() {
  const queryClient = useQueryClient()
  const isOnline = useNetworkStore((s) => s.isOnline)

  const triggerSync = useCallback(() => {
    if (isOnline) syncService.processQueue(queryClient).catch(console.error)
  }, [isOnline, queryClient])

  const create = useCallback(async (data: Partial<Booking>): Promise<string> => {
    const restaurantId = useAppStore.getState().restaurantId
    const tempBooking = buildTempBooking(data)
    const tempId = tempBooking.documentId

    // Write to Dexie — useLiveQuery reacts immediately, no overlay needed
    await db.bookings.put(tempBooking)

    // Strip client-only fields (bookingTables) before sending to server —
    // table associations are created separately via booking-tables API
    const { bookingTables: _bt, ...serverData } = data as any
    await queueManager.enqueue({
      method: 'POST',
      path: '/api/bookings',
      body: { data: { ...serverData, bookosId: tempBooking.bookosId, restaurant: restaurantId } },
      tempId,
    })

    eventBus.emit('booking:queued', { tempId })
    triggerSync()
    return tempId
  }, [triggerSync])

  const update = useCallback(async (documentId: string, data: Partial<Booking>): Promise<void> => {
    // Update Dexie immediately
    await db.bookings.update(documentId, data as Partial<Booking>)

    await queueManager.enqueue({
      method: 'PUT',
      path: `/api/bookings/${documentId}`,
      body: { data },
    })
    triggerSync()
  }, [triggerSync])

  const updateStatus = useCallback(
    (documentId: string, status: BookingStatus) =>
      update(documentId, { status } as Partial<Booking>),
    [update],
  )

  const remove = useCallback(async (documentId: string): Promise<void> => {
    // Remove from Dexie immediately
    await db.bookings.delete(documentId)

    await queueManager.enqueue({
      method: 'DELETE',
      path: `/api/bookings/${documentId}`,
    })
    triggerSync()
  }, [triggerSync])

  return { create, update, updateStatus, remove }
}
